import { ref } from 'vue'
import {
  BillingAlertType,
  BillingQueryDocument,
  PlanStatus,
  useCancelSubscriptionMutation,
  type BillingQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { getStripe } from '@/shared/services/stripe'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useWebviewStore } from '@/shared/stores/webview'
import { openModal, Modals } from '@/shared/dialogs/modals'

import { cancelContent } from './content'

export function useCancelPlan() {
  const toastStore = useToastStore()
  const webviewStore = useWebviewStore()

  const { mutate: cancelSubscription } = useCancelSubscriptionMutation()
  const submitting = ref(false)

  const authenticatePayment = async (clientSecret: string) => {
    if (webviewStore.isActive) return true

    const stripe = await getStripe()
    if (!stripe) return true

    const { error } = await stripe.confirmCardPayment(clientSecret)
    if (error) {
      toastStore.showToast({
        type: ToastType.Error,
        content: 'Payment authentication failed',
        error: new Error(error.message),
      })
      return false
    }

    return true
  }

  const submit = async () => {
    submitting.value = true

    try {
      const response = await cancelSubscription(
        {},
        {
          update(cache) {
            cache.updateQuery<BillingQueryQuery>(
              { query: BillingQueryDocument },
              (current) => {
                if (!current) return current
                return {
                  ...current,
                  billing: {
                    ...current.billing,
                    billingAlert: {
                      __typename: 'BillingAlert',
                      isActive: true,
                      type: BillingAlertType.WillCancel,
                    },
                    plan: {
                      ...current.billing.plan,
                      status: PlanStatus.Canceled,
                      dateInfo: {
                        __typename: 'BillingPlanDateInfo',
                        prefix: 'Cancelled on',
                        date: Date.now(),
                      },
                    },
                  },
                }
              }
            )
          },
        }
      )

      const requiresAction =
        response?.data?.cancelSubscription.paymentMethod?.requiresAction
      if (requiresAction?.clientSecret) {
        const authenticated = await authenticatePayment(
          requiresAction.clientSecret
        )
        if (!authenticated) {
          submitting.value = false
          return
        }
      }

      openModal(Modals.Notice, {
        props: {
          title: cancelContent.success.title,
          body: cancelContent.success.body,
        },
      })
    } catch (error) {
      submitting.value = false
      toastStore.showToast({
        type: ToastType.Error,
        content: 'Could not cancel subscription. Please try again later.',
        error: error as Error,
      })
    }
  }

  return { submitting, submit }
}
